import { Plus } from 'lucide-react';
import { Button } from '../button/button.tsx';
import {
  SettingsSection,
  SettingsSectionHeading,
} from '../settings/settings-section.tsx';
import {
  DEFAULT_MEETING_TEMPLATE_LIST_LABELS,
  type MeetingTemplateListLabels,
} from './meeting-template-list-labels.ts';
import type { MeetingTemplateRow } from './meeting-template-types.ts';
import { TemplateListRows } from './template-list-rows.tsx';

/** Rows arranged together: a drag never carries a row out of its own run. */
export interface MeetingTemplateListRun {
  key: string;
  templates: MeetingTemplateRow[];
  canReorder?: boolean;
  /* The whole run, in the order it now stands in. */
  onReorder?: (ids: string[]) => void;
}

/** One list of templates, and what this reader may do with it. */
export interface MeetingTemplateListSection {
  key: string;
  title?: string;
  description?: string;
  /** Shown instead of rows when no run holds any. */
  emptyMessage: string;
  /** Shown one after another, each arranged on its own. */
  runs: MeetingTemplateListRun[];
  canCreate?: boolean;
  canMakeDefault?: boolean;
  onCreate?: () => void;
  /** Names the create button when its own text does not say what it makes. */
  createLabel?: string;
}

export interface MeetingTemplateListProps {
  sections: MeetingTemplateListSection[];
  /** Cards each stand apart; blocks share one card, a line between them. */
  layout?: 'cards' | 'blocks';
  /** Ready to show, above the sections: the host decides what went wrong. */
  error?: string;
  onOpen: (templateId: string) => void;
  onMakeDefault?: (templateId: string) => void;
  labels?: Partial<MeetingTemplateListLabels>;
}

/**
 * Lists of meeting templates (gesprekstypes), one per section. Data,
 * permissions and text all come in as props: the host fetches, routes and
 * decides who may do what.
 */
export function MeetingTemplateList({
  sections,
  layout = 'cards',
  error,
  onOpen,
  onMakeDefault,
  labels,
}: MeetingTemplateListProps) {
  const text = { ...DEFAULT_MEETING_TEMPLATE_LIST_LABELS, ...labels };

  const createButton = (section: MeetingTemplateListSection) =>
    section.canCreate && (
      <Button
        size='sm'
        variant='quiet'
        aria-label={section.createLabel}
        onClick={section.onCreate}
      >
        <Plus aria-hidden='true' className='size-3.5' />
        {text.newTemplate}
      </Button>
    );

  const rows = (section: MeetingTemplateListSection) => {
    const filled = section.runs.filter((run) => run.templates.length > 0);
    if (filled.length === 0) {
      return <p className='px-5.5 py-4 text-sm text-ink-muted'>{section.emptyMessage}</p>;
    }
    return filled.map((run) => (
      <TemplateListRows
        key={run.key}
        templates={run.templates}
        labels={text}
        sortable={run.canReorder ?? false}
        canMakeDefault={section.canMakeDefault ?? false}
        onOpen={onOpen}
        onMakeDefault={onMakeDefault}
        onReorder={run.onReorder}
      />
    ));
  };

  return (
    <>
      {error && (
        <p role='alert' className='mb-3 text-base text-danger'>
          {error}
        </p>
      )}

      {layout === 'blocks' ? (
        <SettingsSection>
          {sections.map((section) => (
            <div
              key={section.key}
              data-slot='template-block'
              className='border-t border-line first:border-t-0'
            >
              <SettingsSectionHeading
                title={section.title}
                description={section.description}
                action={createButton(section)}
              />
              <div>{rows(section)}</div>
            </div>
          ))}
        </SettingsSection>
      ) : (
        sections.map((section) => (
          <SettingsSection
            key={section.key}
            title={section.title}
            description={section.description}
            action={createButton(section)}
          >
            {rows(section)}
          </SettingsSection>
        ))
      )}
    </>
  );
}
